import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, ChevronDown, Menu, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import LoanCalculator from '../components/LoanCalculator';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Home = () => {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0D47A1]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0D47A1] border-b border-[#00E676]/20 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <button onClick={scrollToTop} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <img
                  src="/flexisolutions-5.png"
                  alt="Flex Solution Logo"
                  className="w-[97px] h-[97px] drop-shadow-lg"
                />
                <span className="text-3xl font-bold text-white whitespace-nowrap">{t('header.brandName')}</span>
              </button>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('calculator')} className="text-gray-200 hover:text-[#FFD600] font-medium transition-colors">{t('header.nav.calculator')}</button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-gray-200 hover:text-[#FFD600] font-medium transition-colors">{t('header.nav.howItWorks')}</button>
              <button onClick={() => scrollToSection('benefits')} className="text-gray-200 hover:text-[#FFD600] font-medium transition-colors">{t('header.nav.benefits')}</button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-200 hover:text-[#FFD600] font-medium transition-colors">{t('header.nav.faq')}</button>
              <LanguageSwitcher />
              <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="bg-[#FF5722] hover:bg-[#f4511e] text-white px-6 py-2 rounded-lg font-semibold transition-colors shadow-lg">{t('header.applyNow')}</Link>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-white">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#00E676]/20 bg-[#0D47A1]">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('calculator')} className="block w-full text-left text-gray-200 hover:text-[#FFD600] font-medium py-2">{t('header.nav.calculator')}</button>
              <button onClick={() => scrollToSection('how-it-works')} className="block w-full text-left text-gray-200 hover:text-[#FFD600] font-medium py-2">{t('header.nav.howItWorks')}</button>
              <button onClick={() => scrollToSection('benefits')} className="block w-full text-left text-gray-200 hover:text-[#FFD600] font-medium py-2">{t('header.nav.benefits')}</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-gray-200 hover:text-[#FFD600] font-medium py-2">{t('header.nav.faq')}</button>
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="block w-full text-center bg-[#FF5722] hover:bg-[#f4511e] text-white px-6 py-2 rounded-lg font-semibold shadow-lg">{t('header.applyNow')}</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/couple-flexi-solutions.jpg"
            alt="Couple using Flex Solution"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D47A1]/95 via-[#0D47A1]/90 to-[#0D47A1]/85"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('calculator')} className="bg-[#FF5722] hover:bg-[#f4511e] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2">
                {t('hero.calculateButton')} <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="bg-transparent hover:bg-white/10 text-white px-8 py-4 rounded-lg font-semibold text-lg border-2 border-[#FFD600] transition-all duration-200">
                {t('hero.learnMore')}
              </button>
            </div>
            <div className="flex items-center gap-8 mt-12">
              <div>
                <div className="text-3xl font-bold text-[#FFD600]">{t('hero.stats.customers.value')}</div>
                <div className="text-sm text-gray-200">{t('hero.stats.customers.label')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#FFD600]">{t('hero.stats.approval.value')}</div>
                <div className="text-sm text-gray-200">{t('hero.stats.approval.label')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#FFD600]">{t('hero.stats.rating.value')}</div>
                <div className="text-sm text-gray-200">{t('hero.stats.rating.label')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section className="py-16 bg-gradient-to-r from-[#FF5722] to-[#E040FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="text-center text-white">
              <div className="text-4xl font-bold mb-2">{t('technology.digitalProcess.value')}</div>
              <div className="text-sm text-white/90">{t('technology.digitalProcess.label')}</div>
            </div>
            <div className="text-center text-white">
              <div className="text-4xl font-bold mb-2">{t('technology.access.value')}</div>
              <div className="text-sm text-white/90">{t('technology.access.label')}</div>
            </div>
            <div className="text-center text-white">
              <div className="text-4xl font-bold mb-2">{t('technology.encryption.value')}</div>
              <div className="text-sm text-white/90">{t('technology.encryption.label')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria Section */}
      <section className="py-20 bg-[#0D47A1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('eligibility.title')}</h2>
            <p className="text-xl text-gray-200">{t('eligibility.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t('eligibility.criteria', { returnObjects: true }).map((criterion, index) => {
              const IconComponent = Icons[criterion.icon.split('-').map((word, i) =>
                word.charAt(0).toUpperCase() + word.slice(1)
              ).join('')];
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-[#FFD600]/30 hover:border-[#FFD600] bg-[#0a3b80] text-center">
                  <CardHeader>
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#FFD600]/20 to-[#00E676]/20 rounded-full flex items-center justify-center mb-4">
                      {IconComponent && <IconComponent className="w-8 h-8 text-[#FFD600]" />}
                    </div>
                    <CardTitle className="text-lg text-white">{criterion.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-200">{criterion.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section id="calculator" className="py-20 bg-[#0D47A1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">{t('calculator.title')}</h2>
            <p className="text-xl text-gray-200">{t('calculator.subtitle')}</p>
          </div>
          <LoanCalculator />
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-[#0a3b80]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('howItWorks.title')}</h2>
            <p className="text-xl text-gray-200">{t('howItWorks.subtitle')}</p>
          </div>

          {/* Process Steps with Images */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-8">
              {t('howItWorks.steps', { returnObjects: true }).map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#00E676] to-[#FFD600] rounded-full flex items-center justify-center text-[#0D47A1] text-xl font-bold">
                      {step.step}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-200">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1681826291722-70bd7e9e6fc3?w=800&q=80"
                alt={t('howItWorks.imageAlt')}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#00E676] p-6 rounded-xl shadow-xl">
                <div className="text-sm text-[#0D47A1]/80 font-medium">{t('howItWorks.badge.text')}</div>
                <div className="text-2xl font-bold text-[#0D47A1]">{t('howItWorks.badge.value')}</div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="inline-block bg-[#FF5722] hover:bg-[#f4511e] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              {t('header.applyNow')}
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-[#0D47A1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('benefits.title')}</h2>
            <p className="text-xl text-gray-200">{t('benefits.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {t('benefits.features', { returnObjects: true }).map((feature, index) => {
                const IconComponent = Icons[feature.icon.split('-').map((word, i) => i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.charAt(0).toUpperCase() + word.slice(1)).join('')];
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-[#00E676]/30 hover:border-[#00E676] bg-[#0a3b80]">
                    <CardHeader>
                      <div className="w-14 h-14 bg-gradient-to-br from-[#00E676]/20 to-[#FFD600]/20 rounded-lg flex items-center justify-center mb-4">
                        {IconComponent && <IconComponent className="w-7 h-7 text-[#00E676]" />}
                      </div>
                      <CardTitle className="text-xl text-white">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-200">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1681826292838-c37fbd22263a?w=800&q=80"
                alt={t('benefits.imageAlt')}
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-6 -right-6 bg-[#E040FB] text-white p-6 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">{t('benefits.badge.value')}</div>
                <div className="text-sm">{t('benefits.badge.label')}</div>
              </div>
            </div>
          </div>

          {/* Mobile CTA */}
          <div className="text-center mt-12 md:hidden">
            <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="inline-block bg-[#FF5722] hover:bg-[#f4511e] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              {t('header.applyNow')}
            </Link>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-[#0a3b80]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('comparison.title')}</h2>
            <p className="text-xl text-gray-200">{t('comparison.subtitle')}</p>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-[#0D47A1] rounded-2xl shadow-xl overflow-hidden border border-[#00E676]/30">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#FF5722] to-[#E040FB]">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">{t('comparison.tableHeaders.feature')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{t('comparison.tableHeaders.traditional')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{t('comparison.tableHeaders.us')}</th>
                </tr>
              </thead>
              <tbody>
                {t('comparison.data', { returnObjects: true }).map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-[#0a3b80]/50' : 'bg-[#0D47A1]'}>
                    <td className="px-6 py-4 font-medium text-white">{row.feature}</td>
                    <td className="px-6 py-4 text-center text-gray-400">{row.traditional}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-2 text-[#00E676] font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        {row.us}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {t('comparison.data', { returnObjects: true }).map((row, index) => (
              <div
                key={index}
                className={`rounded-2xl shadow-xl p-6 border border-[#00E676]/30 ${
                  index % 2 === 0 ? 'bg-[#0a3b80]' : 'bg-[#0D47A1]'
                }`}
              >
                <h3 className="text-lg font-bold text-white mb-4 pb-3 border-b border-[#00E676]/30">
                  {row.feature}
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-gray-300 mb-1">{t('comparison.tableHeaders.traditional')}</div>
                    <div className="text-gray-400">{row.traditional}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-300 mb-1">{t('comparison.tableHeaders.us')}</div>
                    <div className="flex items-center gap-2 text-[#00E676] font-semibold">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{row.us}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile CTA */}
          <div className="text-center mt-12 md:hidden">
            <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="inline-block bg-[#FF5722] hover:bg-[#f4511e] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              {t('header.applyNow')}
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#0D47A1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('testimonials.title')}</h2>
            <p className="text-xl text-gray-200">{t('testimonials.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('testimonials.items', { returnObjects: true }).map((testimonial, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300 border-2 border-[#E040FB]/30 hover:border-[#E040FB] relative overflow-hidden bg-[#0a3b80]">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#E040FB]/20 to-[#FFD600]/20 rounded-bl-full opacity-50"></div>
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF5722] to-[#E040FB] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">{testimonial.name}</CardTitle>
                      <p className="text-sm text-gray-300">{testimonial.location}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#FFD600] text-[#FFD600]" />
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-200 italic relative z-10">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-[#0a3b80]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">{t('faq.title')}</h2>
            <p className="text-xl text-gray-200">{t('faq.subtitle')}</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {t('faq.items', { returnObjects: true }).map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-[#0D47A1] border-2 border-[#00E676]/30 rounded-lg px-6 shadow-sm hover:shadow-md hover:border-[#00E676] transition-all">
                <AccordionTrigger className="text-left font-semibold text-white hover:text-[#FFD600]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-200 pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#E040FB] to-[#FFD600] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('cta.title')}</h2>
          <p className="text-xl mb-8 opacity-90">{t('cta.subtitle')}</p>
          <Link to={i18n.language === 'fr' ? '/demandez' : '/apply'} className="inline-block bg-[#0D47A1] text-white hover:bg-[#0a3b80] px-10 py-4 rounded-lg font-bold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
            {t('cta.button')}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#052a5e] text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <button onClick={scrollToTop} className="flex items-center gap-2 mb-4 hover:opacity-80 transition-opacity">
                <img
                  src="/flexisolutions-5.png"
                  alt="Flex Solution Logo"
                  className="w-16 h-16 md:w-[97px] md:h-[97px] drop-shadow-lg"
                />
                <span className="text-xl md:text-2xl font-bold text-white">{t('header.brandName')}</span>
              </button>
              <p className="text-sm text-gray-300 mb-3">{t('footer.tagline')}</p>
              <p className="text-sm text-gray-300">{t('footer.address')}</p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.company.title')}</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@flexsolution.ca" className="hover:text-[#FFD600] transition-colors">{t('footer.company.links.contact')}</a></li>
                <li><a href="mailto:info@flexsolution.ca" className="hover:text-[#FFD600] transition-colors">info@flexsolution.ca</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.resources.title')}</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-[#FFD600] transition-colors">{t('footer.resources.links.howItWorks')}</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-[#FFD600] transition-colors">{t('footer.resources.links.faq')}</button></li>
                <li><a href="#" className="hover:text-[#FFD600] transition-colors">{t('footer.resources.links.blog')}</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">{t('footer.legal.title')}</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-[#FFD600] transition-colors">{t('footer.legal.links.privacy')}</a></li>
                <li><a href="#" className="hover:text-[#FFD600] transition-colors">{t('footer.legal.links.terms')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#0D47A1] pt-8 text-center text-sm">
            <p>{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;